import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { getFirestore, doc, getDoc } from 'firebase/firestore';
import { db } from '../firebase';

const MarsHub = () => {
  const navigate = useNavigate();
  const [marsPhotos, setMarsPhotos] = useState([]);
  const [roverData, setRoverData] = useState(null);
  const [selectedRover, setSelectedRover] = useState('perseverance');
  const [photoRover, setPhotoRover] = useState('perseverance'); // Separate rover selection for photos
  const [photoSet, setPhotoSet] = useState('today'); // Toggle between today's and yesterday's photos
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  const [lastPhotoUpdate, setLastPhotoUpdate] = useState(null);
  const [dataUsage, setDataUsage] = useState({
    cacheHits: 0
  });
  const [selectedPhoto, setSelectedPhoto] = useState(null); // For photo modal


  const NASA_API_KEY = process.env.REACT_APP_NASA_API_KEY || 'DEMO_KEY';

  // Mars rovers data - UNIQUE information for each rover
  const rovers = {
    curiosity: {
      name: 'Curiosity Rover',
      launchDate: '2011-11-26',
      landingDate: '2012-08-06',
      status: 'Active',
      mission: 'Mars Science Laboratory (MSL)',
      location: 'Gale Crater',
      locationMapUrl: 'https://mars.nasa.gov/maps/location/?mission=MSL&site=NOW',
      distanceTraveled: '29.1 km',
      sols: '3,851',
      description: 'Largest and most advanced rover ever sent to Mars. Still actively exploring and conducting science experiments.',
      objectives: 'Search for signs of ancient life, study Martian geology and climate',
      instruments: 'Chemistry laboratory, weather station, radiation detector, cameras',
      hasPhotos: true,
      photoCount: '1,000+',
      latestPhotoDate: '2024'
    },
    perseverance: {
      name: 'Perseverance Rover',
      launchDate: '2020-07-30',
      landingDate: '2021-02-18',
      status: 'Active',
      mission: 'Mars 2020',
      location: 'Jezero Crater',
      locationMapUrl: 'https://mars.nasa.gov/maps/location/?mission=M20&site=NOW',
      distanceTraveled: '22.8 km',
      sols: '1,082',
      description: 'NASA\'s most advanced rover, designed to search for signs of ancient microbial life and collect rock samples for future return to Earth.',
      objectives: 'Search for ancient life, collect samples for return, test oxygen production',
      instruments: 'SuperCam, PIXL, SHERLOC, MOXIE, cameras, sample collection system',
      hasPhotos: true,
      photoCount: '100,000+',
      latestPhotoDate: '2024'
    },
    opportunity: {
      name: 'Opportunity Rover',
      launchDate: '2003-07-07',
      landingDate: '2004-01-25',
      status: 'Inactive (Lost Contact)',
      mission: 'Mars Exploration Rover (MER-B)',
      location: 'Meridiani Planum',
      distanceTraveled: '45.16 km',
      sols: '5,352',
      description: 'Twin rover to Spirit that exceeded all expectations. Explored Mars for 14 years, 5 months before losing contact during a dust storm.',
      objectives: 'Search for evidence of past water, study Martian geology',
      instruments: 'Panoramic camera, miniature thermal emission spectrometer, alpha particle X-ray spectrometer',
      hasPhotos: false,
      photoCount: 'N/A',
      latestPhotoDate: '2018'
    },
    spirit: {
      name: 'Spirit Rover',
      launchDate: '2003-06-10',
      landingDate: '2004-01-04',
      status: 'Inactive (Stuck in Soil)',
      mission: 'Mars Exploration Rover (MER-A)',
      location: 'Gusev Crater',
      distanceTraveled: '7.73 km',
      sols: '2,269',
      description: 'First rover to climb a hill on Mars. Got stuck in soft soil and lost contact after 6 years of operation.',
      objectives: 'Search for evidence of past water, study Martian geology',
      instruments: 'Panoramic camera, miniature thermal emission spectrometer, alpha particle X-ray spectrometer, rock abrasion tool',
      hasPhotos: false,
      photoCount: 'N/A',
      latestPhotoDate: '2010'
    }
  };

  // Rovers with available photos (only active ones with actual photo APIs)
  const photoRovers = {
    perseverance: rovers.perseverance,
    curiosity: rovers.curiosity
  };

  // Mars facts
  const marsFacts = [
    { title: 'Distance from Earth', value: '54.6 million km (current)', detail: 'Varies from 54.6M to 401M km' },
    { title: 'Day Length', value: '24 hours, 37 minutes', detail: 'One Mars day (sol) = 24h 37m 22.7s' },
    { title: 'Year Length', value: '687 Earth days', detail: 'Mars takes 687 Earth days to orbit the Sun' },
    { title: 'Gravity', value: '38% of Earth', detail: 'A 100kg person would weigh 38kg on Mars' },
    { title: 'Atmosphere', value: '95% CO₂', detail: 'Very thin atmosphere, mostly carbon dioxide' },
    { title: 'Temperature', value: '-65°C average', detail: 'Ranges from -87°C to -5°C' },
    { title: 'Moons', value: '2 (Phobos & Deimos)', detail: 'Small potato-shaped moons' },
    { title: 'Diameter', value: '6,779 km', detail: 'About half the size of Earth' }
  ];

  // Mars missions timeline
  const marsMissions = [
    { year: '1964', mission: 'Mariner 4', type: 'Flyby', status: 'Success', description: 'First successful Mars flyby' },
    { year: '1971', mission: 'Mariner 9', type: 'Orbiter', status: 'Success', description: 'First Mars orbiter' },
    { year: '1976', mission: 'Viking 1 & 2', type: 'Lander', status: 'Success', description: 'First successful Mars landing' },
    { year: '1997', mission: 'Mars Pathfinder', type: 'Rover', status: 'Success', description: 'First rover (Sojourner)' },
    { year: '2004', mission: 'Spirit & Opportunity', type: 'Rovers', status: 'Success', description: 'Twin rovers explored for years' },
    { year: '2012', mission: 'Curiosity', type: 'Rover', status: 'Active', description: 'Largest rover, still exploring' },
    { year: '2021', mission: 'Perseverance', type: 'Rover', status: 'Active', description: 'Sample collection for return mission' },
    { year: '2022', mission: 'Tianwen-1', type: 'Orbiter/Lander/Rover', status: 'Active', description: 'China\'s first Mars mission' }
  ];

  // State to track if we've already loaded data
  const [dataLoaded, setDataLoaded] = useState(false);
  const [photosLoaded, setPhotosLoaded] = useState(false);

  // Function to calculate Mars sols since landing
  const calculateMarsSols = (landingDate) => {
    const landing = new Date(landingDate);
    const now = new Date();

    // Mars sol is 24 hours 39 minutes 35 seconds = 88775.244 seconds
    const marsSolSeconds = 24 * 3600 + 39 * 60 + 35.244; // 88775.244 seconds

    const timeDiffMs = now - landing;
    const timeDiffSeconds = timeDiffMs / 1000;

    const sols = Math.floor(timeDiffSeconds / marsSolSeconds);
    return sols.toLocaleString(); // Add commas for readability
  };

  // Function to get today's date in YYYY format
  const getTodaysYear = () => {
    return new Date().getFullYear().toString();
  };

  // Function to fix double https URLs
  const fixDoubleHttps = (url) => {
    if (url && url.startsWith('https://https://')) {
      return url.replace('https://https://', 'https://');
    }
    return url;
  };

  // Fetch Mars data from Firestore (no photos on initial load)
  const fetchMarsDataFromFirestore = useCallback(async () => {
    if (dataLoaded) return; // Prevent multiple fetches

    setLoading(true);
    try {
      setRoverData(rovers[selectedRover]);
      setDataLoaded(true); // Mark as loaded to prevent further fetches
    } catch (error) {
      console.error('Error fetching Mars data from Firestore:', error);
    } finally {
      setLoading(false);
    }
  }, [selectedRover, rovers, dataLoaded]);

  // Fetch photos for a specific rover (used when photo rover or photo set changes)
  const fetchPhotosForRover = useCallback(async (rover, set = 'today') => {
    try {
      console.log(`📸 Fetching ${rover} ${set} photos from Firestore...`);
      const docName = set === 'yesterday' ? `photos_${rover}_yesterday` : `photos_${rover}`;
      const photosDoc = await getDoc(doc(db, 'marsData', docName));

      if (photosDoc.exists()) {
        const photosData = photosDoc.data();
        setMarsPhotos(photosData.photos || []);
        setLastPhotoUpdate(photosData.lastUpdated?.toDate() || new Date());
        setDataUsage(prev => ({ ...prev, cacheHits: prev.cacheHits + 1 }));

        console.log(`✅ ${rover} ${set} photos loaded from Firestore: ${photosData.photos?.length || 0} photos`);
      } else {
        console.log(`${rover} ${set} photos not found in Firestore`);
        setMarsPhotos([]);
      }
    } catch (error) {
      console.log(`Error fetching ${rover} ${set} photos from Firestore:`, error.message);
      setMarsPhotos([]);
    }
  }, []);

  useEffect(() => {
    fetchMarsDataFromFirestore();
  }, [fetchMarsDataFromFirestore]);

  // Effect to handle photo fetching when on photos tab or when parameters change
  useEffect(() => {
    if (activeTab === 'photos') {
      fetchPhotosForRover(photoRover, photoSet);
    }
  }, [activeTab, photoRover, photoSet, fetchPhotosForRover]);





  const getStatusColor = (status) => {
    switch (status.toLowerCase()) {
      case 'active': return 'text-green-400';
      case 'success': return 'text-green-400';
      case 'inactive': return 'text-red-400';
      default: return 'text-yellow-400';
    }
  };

  const tabs = [
    { id: 'overview', label: 'Overview', icon: '🌍' },
    { id: 'rovers', label: 'Rovers', icon: '🚗' },
    { id: 'missions', label: 'Missions', icon: '🚀' },
    { id: 'weather', label: 'Weather', icon: '🌡️' },
    { id: 'photos', label: 'Photos', icon: '📸' }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-b from-red-900 via-orange-900 to-black text-white">
      {/* Header - Add top padding to account for sticky navigation */}
      <div className="relative overflow-hidden pt-2">
        <div className="absolute inset-0 bg-black bg-opacity-50"></div>
        <div className="relative z-10 p-8 text-center">
          <h1 className="text-5xl font-bold mb-4 text-red-400">Mars Exploration Hub</h1>
          <p className="text-xl text-gray-300 max-w-3xl mx-auto">
            Explore the Red Planet through NASA's eyes. Discover rovers, missions, weather, and breathtaking images from Mars.
          </p>
        </div>
      </div>

      {/* Mars Globe Explorer CTA - Exciting interactive button */}
      <div className="flex justify-center px-4 relative z-20 -mt-2 mb-8">
        <div className="bg-gradient-to-r from-red-900 via-orange-900 to-red-900 rounded-2xl px-8 py-4 shadow-2xl border-2 border-red-500 hover:border-red-400 transition-all duration-500 transform hover:scale-105 max-w-5xl">
          <div className="text-center">
            <h2 className="text-3xl font-bold text-white mb-3">Experience Mars Like Never Before!</h2>
            <p className="text-gray-200 mb-6 text-lg">
              Step into an immersive 3D journey across the Red Planet. Rotate, zoom, and explore Mars in stunning detail with our interactive globe visualizer.
            </p>
            <button
              onClick={() => navigate('/mars-globe')}
              className="bg-gradient-to-r from-red-600 to-orange-600 hover:from-red-500 hover:to-orange-500 text-white px-14 py-3 rounded-xl font-bold text-xl shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-110 animate-pulse mt-4"
            >
              🚀 Launch Mars Explorer
            </button>
          </div>
        </div>
      </div>

      {/* Navigation Tabs - Ensure it stays on top */}
      <div className="flex justify-center mb-0 px-4 relative z-20">
        <div className="bg-black bg-opacity-50 rounded-lg p-2 flex flex-wrap gap-2">
          {tabs.map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`px-4 py-2 rounded-md transition-all duration-300 flex items-center gap-2 ${
                activeTab === tab.id
                  ? 'bg-red-600 text-white'
                  : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
              }`}
            >
              <span>{tab.icon}</span>
              <span className="hidden sm:inline">{tab.label}</span>
            </button>
          ))}
        </div>
      </div>



      {/* Content - Ensure it stays behind navigation */}
      <div className="container mx-auto px-4 pb-8 pt-4 relative z-10">
        {activeTab === 'overview' && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {marsFacts.map((fact, index) => (
              <div key={index} className="bg-black bg-opacity-50 rounded-lg p-6 hover:bg-opacity-70 transition-all duration-300">
                <h3 className="text-lg font-semibold text-red-400 mb-2">{fact.title}</h3>
                <p className="text-2xl font-bold text-white mb-1">{fact.value}</p>
                <p className="text-sm text-gray-400">{fact.detail}</p>
              </div>
            ))}
          </div>
        )}

        {activeTab === 'rovers' && (
          <div className="space-y-8">
            {/* Rover Selector */}
            <div className="flex justify-center mb-4">
              <div className="bg-gradient-to-r from-orange-900 to-red-900 rounded-xl p-3 flex flex-wrap gap-3 shadow-lg">
                {['perseverance', 'curiosity', 'opportunity', 'spirit'].map(rover => (
                  <button
                    key={rover}
                    onClick={() => {
                      setSelectedRover(rover);
                      setRoverData(rovers[rover]); // Update rover data immediately
                    }}
                    className={`px-6 py-3 rounded-lg font-semibold text-sm transition-all duration-300 transform hover:scale-105 ${
                      selectedRover === rover
                        ? 'bg-white text-orange-900 shadow-md'
                        : 'bg-orange-800 text-white hover:bg-orange-700'
                    }`}
                  >
                    🚗 {rovers[rover].name}
                  </button>
                ))}
              </div>
            </div>

            {/* Rover Details */}
            {roverData && (
              <div className="bg-black bg-opacity-50 rounded-lg p-8">
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                  <div>
                    <h2 className="text-3xl font-bold text-red-400 mb-4">{roverData.name}</h2>
                    <p className="text-gray-300 mb-6">{roverData.description}</p>

                    <div className="grid grid-cols-2 gap-4">
                      <div className="bg-gray-800 rounded-lg p-4">
                        <h3 className="text-sm text-gray-400 mb-1">Status</h3>
                        <p className={`text-lg font-semibold ${getStatusColor(roverData.status)}`}>
                          {roverData.status}
                        </p>
                      </div>
                      <div className="bg-gray-800 rounded-lg p-4">
                        <h3 className="text-sm text-gray-400 mb-1">Mission</h3>
                        <p className="text-lg font-semibold text-white">{roverData.mission}</p>
                      </div>
                      <div className="bg-gray-800 rounded-lg p-4">
                        <h3 className="text-sm text-gray-400 mb-1">Location</h3>
                        {roverData.status === 'Active' ? (
                          <>
                            <p className="text-lg font-semibold text-white mb-3">{roverData.location}</p>
                            <button
                              onClick={() => window.open(roverData.locationMapUrl, '_blank')}
                              className="w-full bg-gradient-to-r from-red-600 to-orange-600 hover:from-red-500 hover:to-orange-500 text-white px-6 py-3 rounded-lg font-semibold text-lg shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105 animate-pulse"
                            >
                              🗺️ Click to see where {selectedRover === 'curiosity' ? 'Curiosity' : 'Perseverance'} is now
                            </button>
                          </>
                        ) : (
                          <p className="text-lg font-semibold text-white">{roverData.location}</p>
                        )}
                      </div>
                      <div className="bg-gray-800 rounded-lg p-4">
                        <h3 className="text-sm text-gray-400 mb-1">Distance</h3>
                        <p className="text-lg font-semibold text-white">{roverData.distanceTraveled}</p>
                      </div>
                      <div className="bg-gray-800 rounded-lg p-4">
                        <h3 className="text-sm text-gray-400 mb-1">Launch Date</h3>
                        <p className="text-sm font-semibold text-white">{new Date(roverData.launchDate).toLocaleDateString()}</p>
                      </div>
                      <div className="bg-gray-800 rounded-lg p-4">
                        <h3 className="text-sm text-gray-400 mb-1">Mars Sols</h3>
                        <p className="text-lg font-semibold text-white">{calculateMarsSols(roverData.landingDate)}</p>
                      </div>
                    </div>
                  </div>

                  <div>
                    <h3 className="text-xl font-semibold text-red-400 mb-4">Mission Highlights</h3>
                    <div className="space-y-3">
                      <div className="bg-gray-800 rounded-lg p-4">
                        <h4 className="text-white font-semibold">Scientific Objectives</h4>
                        <p className="text-gray-300 text-sm mt-1">
                          {selectedRover === 'curiosity' && "Study Mars' geology and climate, search for organic compounds"}
                          {selectedRover === 'perseverance' && "Search for signs of ancient life, collect rock and soil samples"}
                          {selectedRover === 'opportunity' && "Explore Mars' geology, mineralogy, and potential for past water"}
                          {selectedRover === 'spirit' && "Study Mars' geology and mineralogy, search for signs of past water"}
                        </p>
                      </div>
                      <div className="bg-gray-800 rounded-lg p-4">
                        <h4 className="text-white font-semibold">Key Discoveries</h4>
                        <p className="text-gray-300 text-sm mt-1">
                          {selectedRover === 'curiosity' && "Evidence of ancient lake, organic molecules, seasonal methane"}
                          {selectedRover === 'perseverance' && "Ancient river delta, potential biosignatures, sample collection"}
                          {selectedRover === 'opportunity' && "Past water activity, diverse mineralogy, layered rocks"}
                          {selectedRover === 'spirit' && "Hydrothermal systems, volcanic rocks, silica deposits"}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {activeTab === 'missions' && (
          <div className="space-y-6">
            <h2 className="text-3xl font-bold text-center text-red-400 mb-8">Mars Mission Timeline</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {marsMissions.map((mission, index) => (
                <div key={index} className="bg-black bg-opacity-50 rounded-lg p-6 hover:bg-opacity-70 transition-all duration-300">
                  <div className="flex justify-between items-start mb-3">
                    <h3 className="text-xl font-semibold text-white">{mission.mission}</h3>
                    <span className={`text-sm px-2 py-1 rounded ${getStatusColor(mission.status)} bg-gray-800`}>
                      {mission.status}
                    </span>
                  </div>
                  <p className="text-red-400 font-semibold mb-2">{mission.year} • {mission.type}</p>
                  <p className="text-gray-300 text-sm">{mission.description}</p>
                </div>
              ))}
            </div>
          </div>
        )}

        {activeTab === 'weather' && (
          <div className="max-w-6xl mx-auto">
            <h2 className="text-3xl font-bold text-center text-red-400 mb-8">Mars Weather</h2>

            {/* Introduction */}
            <div className="bg-black bg-opacity-50 rounded-lg p-8 mb-8 text-center">
              <div className="mb-6">
                <div className="text-6xl mb-4">🌌</div>
                <h3 className="text-2xl font-bold text-red-400 mb-2">Mars Climate Overview</h3>
                <p className="text-gray-300 mb-4">
                  Mars has an extreme climate with temperatures ranging from -87°C to -5°C.
                  The thin atmosphere (only 1% of Earth's pressure) creates dramatic temperature swings and allows for massive dust storms.
                </p>
                <p className="text-gray-400 text-sm mb-6">
                  Live weather data from NASA's Mars rovers is currently unavailable due to changes in NASA's data services.
                  Below you'll find typical weather conditions on Mars based on historical data.
                </p>
              </div>

              {/* NASA Weather Page Button */}
              <button
                onClick={() => window.open('https://mars.nasa.gov/layout/embed/image/mslweather/', '_blank')}
                className="px-8 py-3 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors font-semibold text-lg shadow-lg hover:shadow-xl"
              >
                🌐 View NASA's Mars Weather Page
              </button>
              <p className="text-gray-400 text-xs mt-2">Opens in new tab</p>
            </div>

            {/* Typical Mars Weather Conditions */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8">
              {/* Temperature Section */}
              <div className="bg-black bg-opacity-50 rounded-lg p-8">
                <h3 className="text-2xl font-bold text-red-400 mb-6 text-center">🌡️ Temperature</h3>
                <div className="space-y-6">
                  <div className="bg-gray-800 rounded-lg p-6 text-center">
                    <h4 className="text-white font-semibold mb-2">Average Surface Temperature</h4>
                    <p className="text-5xl font-bold text-red-400 mb-2">-65°C</p>
                    <p className="text-gray-400 text-sm">Global average</p>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div className="bg-gray-800 rounded-lg p-4 text-center">
                      <h4 className="text-white font-semibold mb-2">Daily Low</h4>
                      <p className="text-3xl font-bold text-blue-400">-87°C</p>
                      <p className="text-gray-400 text-sm">Night time minimum</p>
                    </div>
                    <div className="bg-gray-800 rounded-lg p-4 text-center">
                      <h4 className="text-white font-semibold mb-2">Daily High</h4>
                      <p className="text-3xl font-bold text-orange-400">-5°C</p>
                      <p className="text-gray-400 text-sm">Day time maximum</p>
                    </div>
                  </div>

                  <div className="bg-gray-800 rounded-lg p-4">
                    <h4 className="text-white font-semibold mb-2">Temperature Facts</h4>
                    <ul className="text-gray-300 text-sm space-y-1">
                      <li>• Mars experiences extreme temperature swings of up to 80°C in a single day</li>
                      <li>• The thin atmosphere provides little insulation against temperature changes</li>
                      <li>• Polar regions can reach -125°C during winter</li>
                      <li>• Equatorial regions warm up to 20°C during summer days</li>
                    </ul>
                  </div>
                </div>
              </div>

              {/* Atmosphere & Wind Section */}
              <div className="bg-black bg-opacity-50 rounded-lg p-8">
                <h3 className="text-2xl font-bold text-red-400 mb-6 text-center">💨 Atmosphere & Wind</h3>
                <div className="space-y-6">
                  <div className="bg-gray-800 rounded-lg p-6 text-center">
                    <h4 className="text-white font-semibold mb-2">Atmospheric Pressure</h4>
                    <p className="text-4xl font-bold text-green-400">6 mbar</p>
                    <p className="text-gray-400 text-sm">Average surface pressure</p>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div className="bg-gray-800 rounded-lg p-4 text-center">
                      <h4 className="text-white font-semibold mb-2">Wind Speed</h4>
                      <p className="text-3xl font-bold text-yellow-400">2-18 m/s</p>
                      <p className="text-gray-400 text-sm">Typical range</p>
                    </div>
                    <div className="bg-gray-800 rounded-lg p-4 text-center">
                      <h4 className="text-white font-semibold mb-2">Atmosphere</h4>
                      <p className="text-3xl font-bold text-purple-400">95% CO₂</p>
                      <p className="text-gray-400 text-sm">Carbon dioxide dominant</p>
                    </div>
                  </div>

                  <div className="bg-gray-800 rounded-lg p-4">
                    <h4 className="text-white font-semibold mb-2">Atmospheric Facts</h4>
                    <ul className="text-gray-300 text-sm space-y-1">
                      <li>• Mars' atmosphere is 100 times thinner than Earth's</li>
                      <li>• 95% carbon dioxide, 3% nitrogen, 1.6% argon</li>
                      <li>• Dust devils (mini-tornadoes) are common and can reach 8km high</li>
                      <li>• Global dust storms can last for months and cover the entire planet</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>

            {/* Mars Weather Phenomena */}
            <div className="bg-black bg-opacity-50 rounded-lg p-8 mb-8">
              <h3 className="text-2xl font-bold text-red-400 mb-6 text-center">🌪️ Mars Weather Phenomena</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                <div className="bg-gray-800 rounded-lg p-6">
                  <h4 className="text-lg font-semibold text-white mb-3">🌪️ Dust Storms</h4>
                  <p className="text-gray-300 text-sm mb-3">
                    Massive dust storms can engulf the entire planet, blocking sunlight and lasting for months.
                    These storms can affect rover operations and solar power generation.
                  </p>
                  <p className="text-blue-400 text-xs">Most intense during southern summer</p>
                </div>

                <div className="bg-gray-800 rounded-lg p-6">
                  <h4 className="text-lg font-semibold text-white mb-3">🌪️ Dust Devils</h4>
                  <p className="text-gray-300 text-sm mb-3">
                    Whirlwinds that pick up dust and sand. They can be seen as dark streaks on the surface
                    and help clean solar panels on rovers.
                  </p>
                  <p className="text-blue-400 text-xs">Common in afternoon when surface heats up</p>
                </div>

                <div className="bg-gray-800 rounded-lg p-6">
                  <h4 className="text-lg font-semibold text-white mb-3">❄️ Frost & Ice</h4>
                  <p className="text-gray-300 text-sm mb-3">
                    Carbon dioxide frost forms during winter nights, especially at the poles.
                    Water ice clouds can form high in the atmosphere.
                  </p>
                  <p className="text-blue-400 text-xs">Polar regions experience seasonal frost</p>
                </div>

                <div className="bg-gray-800 rounded-lg p-6">
                  <h4 className="text-lg font-semibold text-white mb-3">☀️ UV Radiation</h4>
                  <p className="text-gray-300 text-sm mb-3">
                    Without a protective ozone layer, Mars receives intense ultraviolet radiation.
                    This affects surface chemistry and requires radiation protection for equipment.
                  </p>
                  <p className="text-blue-400 text-xs">10-20 times stronger than Earth</p>
                </div>

                <div className="bg-gray-800 rounded-lg p-6">
                  <h4 className="text-lg font-semibold text-white mb-3">🌡️ Seasonal Changes</h4>
                  <p className="text-gray-300 text-sm mb-3">
                    Mars has seasons like Earth due to its axial tilt. However, the elliptical orbit
                    causes seasons to vary greatly in length and intensity.
                  </p>
                  <p className="text-blue-400 text-xs">Southern summer is shorter but hotter</p>
                </div>

                <div className="bg-gray-800 rounded-lg p-6">
                  <h4 className="text-lg font-semibold text-white mb-3">💨 Wind Patterns</h4>
                  <p className="text-gray-300 text-sm mb-3">
                    Surface winds are driven by temperature differences. Strong winds can create
                    sand dunes and affect dust storm formation.
                  </p>
                  <p className="text-blue-400 text-xs">Peak speeds during dust storm season</p>
                </div>
              </div>
            </div>

            {/* Mars vs Earth Comparison */}
            <div className="bg-black bg-opacity-50 rounded-lg p-8">
              <h3 className="text-2xl font-bold text-red-400 mb-6 text-center">🌍 Mars vs Earth: Weather Comparison</h3>
              <div className="overflow-x-auto">
                <table className="w-full bg-gray-800 rounded-lg overflow-hidden">
                  <thead className="bg-gray-700">
                    <tr>
                      <th className="px-6 py-3 text-left text-white font-semibold">Weather Factor</th>
                      <th className="px-6 py-3 text-left text-white font-semibold">Mars</th>
                      <th className="px-6 py-3 text-left text-white font-semibold">Earth</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr className="border-t border-gray-600">
                      <td className="px-6 py-4 text-gray-300 font-medium">Average Temperature</td>
                      <td className="px-6 py-4 text-red-400 font-semibold">-65°C</td>
                      <td className="px-6 py-4 text-green-400 font-semibold">+15°C</td>
                    </tr>
                    <tr className="border-t border-gray-600">
                      <td className="px-6 py-4 text-gray-300 font-medium">Atmospheric Pressure</td>
                      <td className="px-6 py-4 text-red-400 font-semibold">6 mbar</td>
                      <td className="px-6 py-4 text-green-400 font-semibold">1,013 mbar</td>
                    </tr>
                    <tr className="border-t border-gray-600">
                      <td className="px-6 py-4 text-gray-300 font-medium">Atmosphere Composition</td>
                      <td className="px-6 py-4 text-red-400 font-semibold">95% CO₂</td>
                      <td className="px-6 py-4 text-green-400 font-semibold">78% N₂, 21% O₂</td>
                    </tr>
                    <tr className="border-t border-gray-600">
                      <td className="px-6 py-4 text-gray-300 font-medium">Wind Speed</td>
                      <td className="px-6 py-4 text-red-400 font-semibold">2-18 m/s</td>
                      <td className="px-6 py-4 text-green-400 font-semibold">0-15 m/s</td>
                    </tr>
                    <tr className="border-t border-gray-600">
                      <td className="px-6 py-4 text-gray-300 font-medium">UV Radiation</td>
                      <td className="px-6 py-4 text-red-400 font-semibold">10-20x Earth</td>
                      <td className="px-6 py-4 text-green-400 font-semibold">1x (baseline)</td>
                    </tr>
                    <tr className="border-t border-gray-600">
                      <td className="px-6 py-4 text-gray-300 font-medium">Dust Storms</td>
                      <td className="px-6 py-4 text-red-400 font-semibold">Global, months-long</td>
                      <td className="px-6 py-4 text-green-400 font-semibold">Regional, days</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>

            {/* Call to Action */}
            <div className="bg-gradient-to-r from-red-900 to-orange-900 rounded-lg p-8 text-center mt-8">
              <h3 className="text-2xl font-bold text-white mb-4">Want to See Today's Actual Mars Weather?</h3>
              <p className="text-gray-200 mb-6">
                Visit NASA's official Mars weather page for the latest data from Curiosity and Perseverance rovers,
                including current temperatures, atmospheric pressure, and wind measurements.
              </p>
              <button
                onClick={() => window.open('https://mars.nasa.gov/layout/embed/image/mslweather/', '_blank')}
                className="px-8 py-3 bg-white text-red-600 rounded-lg hover:bg-gray-100 transition-colors font-semibold text-lg shadow-lg hover:shadow-xl"
              >
                🚀 Visit NASA Mars Weather
              </button>
            </div>
          </div>
        )}

        {activeTab === 'photos' && (
          <div className="space-y-8">
            <h2 className="text-3xl font-bold text-center text-red-400 mb-8">Mars Rover Photos</h2>

            {/* Photo Rover Selector - Only rovers with available photos */}
            <div className="flex justify-center mb-8">
              <div className="bg-black bg-opacity-50 rounded-lg p-2 flex flex-wrap gap-2">
                {Object.keys(photoRovers).map(rover => (
                  <button
                    key={rover}
                    onClick={() => setPhotoRover(rover)}
                    className={`px-4 py-2 rounded-md transition-all duration-300 ${
                      photoRover === rover
                        ? 'bg-red-600 text-white'
                        : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                    }`}
                  >
                    {photoRovers[rover].name}
                  </button>
                ))}
              </div>
            </div>

            {/* Photos Header with Toggle */}
            <div className="text-center mb-6">
              <h3 className="text-2xl font-bold text-red-400 mb-2">📸 Mars Rover Photos</h3>
              <p className="text-gray-300 mb-4">
                Fresh photos updated daily from NASA's Mars rovers
              </p>

              {/* Photo Set Toggle */}
              <div className="flex justify-center mb-6">
                <div className="bg-black bg-opacity-50 rounded-lg p-2 flex gap-2">
                  <button
                    onClick={() => setPhotoSet('today')}
                    className={`px-4 py-2 rounded-md transition-all duration-300 ${
                      photoSet === 'today'
                        ? 'bg-red-600 text-white'
                        : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                    }`}
                  >
                    Today's Photos
                  </button>
                  <button
                    onClick={() => setPhotoSet('yesterday')}
                    className={`px-4 py-2 rounded-md transition-all duration-300 ${
                      photoSet === 'yesterday'
                        ? 'bg-red-600 text-white'
                        : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                    }`}
                  >
                    Yesterday's Photos
                  </button>
                </div>
              </div>

              {/* Photo Statistics */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 max-w-3xl mx-auto mb-4">
                <div className="bg-gray-800 rounded-lg p-4">
                  <h4 className="text-white font-semibold mb-2">{photoSet === 'today' ? "Today's Photos" : "Yesterday's Photos"}</h4>
                  <p className="text-2xl font-bold text-red-400">{marsPhotos.length}</p>
                  <p className="text-gray-400 text-sm">Curated selection</p>
                </div>
                <div className="bg-gray-800 rounded-lg p-4">
                  <h4 className="text-white font-semibold mb-2">Available Photos</h4>
                  <p className="text-2xl font-bold text-green-400">{rovers[photoRover].photoCount}</p>
                  <p className="text-gray-400 text-sm">From {rovers[photoRover].name}</p>
                </div>
                <div className="bg-gray-800 rounded-lg p-4">
                  <h4 className="text-white font-semibold mb-2">Latest Photos</h4>
                  <p className="text-2xl font-bold text-blue-400">{getTodaysYear()}</p>
                  <p className="text-gray-400 text-sm">Most recent uploads</p>
                </div>
              </div>

              <div className="bg-gray-800 rounded-lg p-4 mb-4">
                <p className="text-gray-300">
                  Viewing <span className="text-red-400 font-semibold">{photoSet}'s photos</span> from{' '}
                  <span className="text-red-400 font-semibold">{rovers[photoRover].name}</span>
                </p>
                {lastPhotoUpdate && (
                  <p className="text-gray-400 text-sm mt-1">
                    Updated daily at midnight UTC • Last updated: {lastPhotoUpdate.toLocaleString()}
                  </p>
                )}
              </div>
            </div>



            {loading ? (
              <div className="text-center py-12">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-red-500 mx-auto mb-4"></div>
                <p className="text-gray-300">Loading Mars photos...</p>
              </div>
            ) : marsPhotos.length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {marsPhotos.map((photo, index) => (
                  <div key={index} className="bg-black bg-opacity-50 rounded-lg overflow-hidden hover:bg-opacity-70 transition-all duration-300 cursor-pointer" onClick={() => setSelectedPhoto(photo)}>
                    <img
                      src={fixDoubleHttps(photo.img_src)}
                      alt={`${photo.rover.name} rover image`}
                      className="w-full h-48 object-cover"
                    />
                    <div className="p-4">
                      <h3 className="text-white font-semibold mb-2">{photo.rover.name}</h3>
                      <p className="text-gray-400 text-sm mb-1">Camera: {photo.camera.full_name}</p>
                      <p className="text-gray-400 text-sm mb-1">Sol: {photo.sol}</p>
                      <p className="text-gray-400 text-sm">Earth Date: {new Date(photo.earth_date).toLocaleDateString()}</p>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-12">
                <p className="text-gray-300 text-lg">No photos available for {rovers[photoRover].name}</p>
                <p className="text-gray-400 mt-2">Try selecting a different rover or check back later.</p>
              </div>
            )}
          </div>
        )}


      </div>

      {/* Photo Modal */}
      {selectedPhoto && (
        <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4" onClick={() => setSelectedPhoto(null)}>
          <div className="bg-black bg-opacity-90 rounded-lg max-w-4xl max-h-[90vh] overflow-hidden" onClick={(e) => e.stopPropagation()}>
            <div className="relative">
              <img
                src={fixDoubleHttps(selectedPhoto.img_src)}
                alt={`${selectedPhoto.rover.name} rover image`}
                className="w-full h-auto max-h-[70vh] object-contain"
              />
              <button
                onClick={() => setSelectedPhoto(null)}
                className="absolute top-4 right-4 bg-black bg-opacity-50 text-white rounded-full w-10 h-10 flex items-center justify-center hover:bg-opacity-70 transition-colors"
              >
                ✕
              </button>
            </div>
            <div className="p-6">
              <h3 className="text-xl font-bold text-red-400 mb-4">{selectedPhoto.rover.name} Rover Photo</h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
                <div>
                  <span className="text-gray-400">Camera:</span>
                  <p className="text-white font-semibold">{selectedPhoto.camera.full_name}</p>
                </div>
                <div>
                  <span className="text-gray-400">Sol:</span>
                  <p className="text-white font-semibold">{selectedPhoto.sol}</p>
                </div>
                <div>
                  <span className="text-gray-400">Earth Date:</span>
                  <p className="text-white font-semibold">{new Date(selectedPhoto.earth_date).toLocaleDateString()}</p>
                </div>
              </div>
              <div className="mt-4 flex justify-end">
                <button
                  onClick={() => setSelectedPhoto(null)}
                  className="px-6 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MarsHub;
